// src/pages/Dashboard/QuickQuote/components/FirstPage.jsx
import React from 'react';
import { quickQuoteApi } from '../../../../api/quickquote/quickQuoteApi';
import styles from './FirstPage.module.css';

const FirstPage = ({ onTransmit, firstPageData, onDataChange, onClearAll }) => {
  const { headerRefNo, headerDiscount, tableData } = firstPageData;

  const handleInputChange = (rowIndex, field, value) => {
    const newTableData = tableData.map((row, index) => 
      index === rowIndex ? { ...row, [field]: value } : row
    );
    
    onDataChange({
      ...firstPageData,
      tableData: newTableData
    });
  };

  const handlePackSizeChange = (rowIndex, field, value) => {
    const newTableData = tableData.map((row, index) => 
      index === rowIndex ? { 
        ...row, 
        packSize: { 
          ...row.packSize, 
          [field]: value 
        } 
      } : row
    );
    
    onDataChange({
      ...firstPageData,
      tableData: newTableData
    });
  };

  const handleHeaderChange = (field, value) => {
    onDataChange({
      ...firstPageData,
      [field]: value
    });
  };

  const handleCasPaste = (e, startIndex) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    const values = pastedText.split(/[\s\n\t]+/).filter(val => val.trim() !== '');
    
    if (values.length > 1) {
      const newTableData = [...tableData];
      values.forEach((value, i) => {
        const targetIndex = startIndex + i;
        if (targetIndex < newTableData.length) {
          newTableData[targetIndex] = { ...newTableData[targetIndex], casNo: value.trim() };
        }
      });
      
      onDataChange({
        ...firstPageData,
        tableData: newTableData
      });
    } else {
      handleInputChange(startIndex, 'casNo', pastedText.trim());
    }
  };

  const handleQuantityPaste = (e, startIndex) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    const lines = pastedText.split(/\n+/).filter(line => line.trim() !== '');
    let values = [];
    
    if (lines.length > 1) {
      values = lines.map(line => line.trim()).filter(val => val !== '');
    } else {
      values = pastedText.split(/\t+/).map(val => val.trim()).filter(val => val !== '');
      
      if (values.length === 1) {
        values = [pastedText.trim()];
      }
    }
    
    if (values.length > 1) {
      const newTableData = [...tableData];
      values.forEach((value, i) => {
        const targetIndex = startIndex + i;
        if (targetIndex < newTableData.length) {
          newTableData[targetIndex] = { 
            ...newTableData[targetIndex], 
            packSize: { 
              ...(newTableData[targetIndex].packSize || {}),
              quantity: value
            } 
          };
        }
      });
      
      onDataChange({
        ...firstPageData,
        tableData: newTableData
      });
    } else {
      handlePackSizeChange(startIndex, 'quantity', pastedText.trim());
    }
  };

  const handleRefNoPaste = (e, startIndex) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    const values = pastedText.split(/[\s\n\t]+/).filter(val => val.trim() !== '');
    
    if (values.length > 1) {
      const newTableData = [...tableData];
      values.forEach((value, i) => {
        const targetIndex = startIndex + i;
        if (targetIndex < newTableData.length) {
          newTableData[targetIndex] = { ...newTableData[targetIndex], refNo: value.trim() };
        }
      });
      
      onDataChange({
        ...firstPageData,
        tableData: newTableData
      });
    } else {
      handleInputChange(startIndex, 'refNo', pastedText.trim());
    }
  };

  const handleDiscountPaste = (e, startIndex) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    const values = pastedText.split(/[\s\n\t]+/).filter(val => val.trim() !== '');
    
    if (values.length > 1) {
      const newTableData = [...tableData];
      values.forEach((value, i) => {
        const targetIndex = startIndex + i;
        if (targetIndex < newTableData.length) {
          newTableData[targetIndex] = { ...newTableData[targetIndex], discount: value.trim() };
        }
      });
      
      onDataChange({
        ...firstPageData,
        tableData: newTableData
      });
    } else {
      handleInputChange(startIndex, 'discount', pastedText.trim());
    }
  };

  const handleTransmit = async () => {
    // Validation 1: At least one CAS entry should be filled
    const rowsWithCasNo = tableData.filter(row => row.casNo && row.casNo.trim() !== '');
    
    if (rowsWithCasNo.length === 0) {
      alert('Validation Error: At least one CAS entry must be filled before transmitting.');
      return;
    }

    // Validation 2: For each CAS entry, there should be a discount (either in header or individual)
    const invalidRows = [];
    rowsWithCasNo.forEach((row, index) => {
      const hasHeaderDiscount = headerDiscount && headerDiscount.trim() !== '';
      const hasIndividualDiscount = row.discount && row.discount.trim() !== '';
      
      if (!hasHeaderDiscount && !hasIndividualDiscount) {
        invalidRows.push(index + 1);
      }
    });

    if (invalidRows.length > 0) {
      alert(`Validation Error: The following rows with CAS entries need a discount (either in header or individual): Row ${invalidRows.join(', ')}`);
      return;
    }

    // Prepare data for transmission
    const transmissionData = tableData.map(row => {
      if (row.casNo && row.casNo.trim() !== '') {
        return {
          ...row,
          discount: (row.discount && row.discount.trim() !== '') ? row.discount : headerDiscount,
          refNo: (row.refNo && row.refNo.trim() !== '') ? row.refNo : headerRefNo,
          packSize: {
            ...(row.packSize || {}),
            quantity: row.packSize?.quantity || '',
            normalizedQuantity: (row.packSize?.quantity || '').toUpperCase().trim()
          }
        };
      }
      return row;
    }).filter(row => row.casNo && row.casNo.trim() !== '');

    onTransmit(transmissionData, { refNo: headerRefNo, discount: headerDiscount });
  };

  const casEntriesCount = tableData.filter(row => row.casNo && row.casNo.trim() !== '').length;
  const pkzEntriesCount = tableData.filter(row => row.packSize && row.packSize.quantity && row.packSize.quantity.toString().trim() !== '').length;

  return (
    <div className={styles.pageContainer}>
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>Quick Quote</h2>
            <p className={styles.subtitle}>Paste the CAS entries, PKZ, Ref no and Discount</p>
          </div>
          <div className={styles.headerButtons}>
            <button onClick={onClearAll} className={styles.clearButton}>
              Clear All
            </button>
            <button onClick={handleTransmit} className={styles.transmitButton}>
              Transmit
            </button>
          </div>
        </div>

        <div className={styles.tableContainer}>
          <div className={styles.tableWrapper}>
            {/* Table Header */}
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th className={styles.th}>
                    <div className={styles.thContent}>
                      CAS Entry
                      <div className={styles.headerPlaceholder}>
                        Paste multiple CAS entries
                      </div>
                    </div>
                  </th>
                  <th className={styles.th}>
                    <div className={styles.thContent}>
                      PKZ
                      <div className={styles.headerPlaceholder}>
                        Paste PKZ
                      </div>
                    </div>
                  </th>
                  <th className={styles.th}>
                    <div className={styles.thContent}>
                      Reference No
                      <input
                        type="text"
                        value={headerRefNo || ''}
                        onChange={(e) => handleHeaderChange('headerRefNo', e.target.value)}
                        className={styles.headerInput}
                        placeholder="Enter to fill all / paste multiple"
                      />
                    </div>
                  </th>
                  <th className={styles.th}>
                    <div className={styles.thContent}>
                      Discount (%)
                      <input
                        type="text"
                        value={headerDiscount || ''}
                        onChange={(e) => handleHeaderChange('headerDiscount', e.target.value)}
                        className={styles.headerInput}
                        placeholder="Enter to fill all / paste multiple"
                      />
                    </div>
                  </th>
                </tr>
              </thead>
            </table>

            {/* Table Body */}
            <div className={styles.tableBody}>
              <table className={styles.table}>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={row.id} className={styles.tableRow}>
                      <td className={styles.td}>
                        <input
                          type="text"
                          value={row.casNo || ''}
                          onChange={(e) => handleInputChange(index, 'casNo', e.target.value)}
                          onPaste={(e) => handleCasPaste(e, index)}
                          className={styles.input}
                          placeholder={`CAS Entry ${index + 1}`}
                        />
                      </td>
                      <td className={styles.td}>
                        <input
                          type="text"
                          value={row.packSize?.quantity || ''}
                          onChange={(e) => handlePackSizeChange(index, 'quantity', e.target.value)}
                          onPaste={(e) => handleQuantityPaste(e, index)}
                          className={styles.input}
                          placeholder="PKZ (e.g., 10 GM, 25 KG)"
                        />
                      </td>
                      <td className={styles.td}>
                        <input
                          type="text"
                          value={row.refNo || ''}
                          onChange={(e) => handleInputChange(index, 'refNo', e.target.value)}
                          onPaste={(e) => handleRefNoPaste(e, index)}
                          className={styles.input}
                          placeholder={`Reference No ${index + 1}`}
                        />
                      </td>
                      <td className={styles.td}>
                        <input
                          type="text"
                          value={row.discount || ''}
                          onChange={(e) => handleInputChange(index, 'discount', e.target.value)}
                          onPaste={(e) => handleDiscountPaste(e, index)}
                          className={styles.input}
                          placeholder={`Discount ${index + 1}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className={styles.footer}>
              <div className={styles.footerStats}>
                Total Rows: <span className={styles.statValue}>{tableData.length}</span> | 
                CAS Entries: <span className={styles.statValue}>{casEntriesCount}</span> |
                PKZ Entries: <span className={styles.statValue}>{pkzEntriesCount}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstPage;


